import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { uniq } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { Link } from 'site-modules/shared/components/link/link';
import { srpLinkBuilderWithParamsConversion } from 'site-modules/shared/utils/srp-link-constructor';
import { BUY_ONLINE_EXIT } from 'client/site-modules/car-buying-online/constants/tracking';
import {
  FACETS_CONFIG,
  FACETS_GROUPS_CONFIG,
} from 'client/site-modules/car-buying-online/constants/usurp-filters-config';
import { InventoryEntities } from 'client/data/models/inventory';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { HOME_DELIVERY } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { SuggestedFacet } from 'site-modules/shared/components/inventory/suggested-facet/suggested-facet';

const FACETS_CONFIG_MAP = {
  facetsConfig: FACETS_CONFIG,
  facetsGroupsConfig: FACETS_GROUPS_CONFIG,
};

const fireTracking = () => {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
    event_data: {
      action_name: TrackingConstant.ACTION_WIDGET_VIEW,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      creative_id: BUY_ONLINE_EXIT,
    },
  });
};

export const SuggestedFacets = ({
  suggestedFacets = [],
  selectedFacets: selectedFacetsProps = {},
  withInStorePurchaseButton = true,
  wrapperClassnames = 'flex-nowrap',
  itemClassnames = '',
  onSuggestedFacetClick,
}) => {
  const [selectionOrder, setSelectionOrder] = useState([]);

  useEffect(() => {
    fireTracking();
  }, []);

  const onFacetClick = useCallback(
    (type, filter, isChecked) => {
      if (filter && filter.name) {
        setSelectionOrder(currentSlectionOrder =>
          isChecked
            ? [...currentSlectionOrder, filter.name]
            : currentSlectionOrder.filter(selectedFacetName => selectedFacetName !== filter.name)
        );
      }
      onSuggestedFacetClick(type, filter, isChecked);
    },
    [onSuggestedFacetClick]
  );

  const facets = useMemo(() => {
    const restFacets = suggestedFacets.filter(facet => !facet.selected);
    const selectedFacets = uniq(
      selectionOrder
        .map(selectedFacetName =>
          suggestedFacets.find(facet => facet && facet.name === selectedFacetName && !!facet.selected)
        )
        // facets that were selected on page load
        .concat(suggestedFacets.filter(facet => !!facet.selected && facet.type !== HOME_DELIVERY))
        .filter(facet => !!facet)
    );

    return [...selectedFacets, ...restFacets];
  }, [selectionOrder, suggestedFacets]);

  return (
    <ul
      className={classnames('suggested-facets list-unstyled d-flex mb-0', wrapperClassnames)}
      data-tracking-parent="edm-entry-suggested-facets"
    >
      {withInStorePurchaseButton && (
        <li className="me-0_5">
          <Button
            tag={Link}
            to={srpLinkBuilderWithParamsConversion({ ...selectedFacetsProps, inventorytype: 'used,cpo' })}
            rel="nofollow"
            aria-label="Apply srp filter"
            className="suggested-facet d-flex align-items-center size-14 fw-normal text-capitalize bg-cool-gray-100 text-cool-gray-30 px-0_75"
            data-tracking-id="view_search_result_in_store"
            data-tracking-parent={BUY_ONLINE_EXIT}
          >
            Include In-store Purchase
          </Button>
        </li>
      )}

      {facets.map(filter => (
        <li key={`${filter.type}-${filter.name}`} className={classnames('me-0_5', itemClassnames)}>
          <SuggestedFacet filter={filter} onClick={onFacetClick} facetsConfigMap={FACETS_CONFIG_MAP} />
        </li>
      ))}
    </ul>
  );
};

SuggestedFacets.propTypes = {
  suggestedFacets: PropTypes.arrayOf(InventoryEntities.FacetValue),
  onSuggestedFacetClick: PropTypes.func.isRequired,
  selectedFacets: PropTypes.shape({}),
  withInStorePurchaseButton: PropTypes.bool,
  wrapperClassnames: PropTypes.string,
  itemClassnames: PropTypes.string,
};
